import { chapters, playerColors, playerRoles } from "./content.js";
export const GAME_TITLE = "Gunnrats23";
export function createInitialState() {
    return {
        chapterIndex: 0,
        taskIndex: 0,
        selectedIds: [],
        score: 0,
        mistakes: 0,
        completedChapterIds: [],
        journal: [],
        phase: "playing",
        log: "Boot sequence ready. The lab computer is pretending to be calm."
    };
}
export function getCurrentChapter(state) {
    const chapter = chapters[Math.min(state.chapterIndex, chapters.length - 1)] ?? chapters[0];
    if (!chapter) {
        throw new Error("Gunnrats23 has no chapters configured.");
    }
    return chapter;
}
export function getCurrentTask(state) {
    const chapter = getCurrentChapter(state);
    const task = chapter.tasks[Math.min(state.taskIndex, chapter.tasks.length - 1)] ?? chapter.tasks[0];
    if (!task) {
        throw new Error(`Chapter ${chapter.id} has no tasks configured.`);
    }
    return task;
}
export function applyAction(state, action) {
    if (action.type === "reset") {
        return createInitialState();
    }
    if (action.type === "setChapter") {
        const chapterIndex = clamp(action.chapterIndex, 0, chapters.length - 1);
        return {
            ...state,
            chapterIndex,
            taskIndex: 0,
            selectedIds: [],
            phase: "playing",
            log: `Loaded chapter ${chapterIndex + 1}: ${chapters[chapterIndex]?.title ?? "Unknown"}.`
        };
    }
    if (state.phase === "finished" && action.type !== "nextChapter") {
        return state;
    }
    if (action.type === "toggle") {
        const task = getCurrentTask(state);
        if (!task.choices.some((choice) => choice.id === action.choiceId)) {
            return { ...state, log: "That card is not on this board." };
        }
        const selected = new Set(state.selectedIds);
        if (selected.has(action.choiceId)) {
            selected.delete(action.choiceId);
        }
        else {
            selected.add(action.choiceId);
        }
        return {
            ...state,
            selectedIds: Array.from(selected),
            log: selected.has(action.choiceId) ? "Card selected." : "Card removed."
        };
    }
    if (action.type === "submit") {
        return submitTask(state);
    }
    if (action.type === "nextChapter") {
        return nextChapter(state);
    }
    return state;
}
function submitTask(state) {
    if (state.phase !== "playing") {
        return state;
    }
    const chapter = getCurrentChapter(state);
    const task = getCurrentTask(state);
    const isCorrect = sameSet(state.selectedIds, task.correctIds);
    if (!isCorrect) {
        return {
            ...state,
            mistakes: state.mistakes + 1,
            score: Math.max(0, state.score - 20),
            log: `${task.failure} Hint: ${task.hint}`
        };
    }
    const nextTaskIndex = state.taskIndex + 1;
    const journal = uniqueAppend(state.journal, `${chapter.title}: ${task.concept}`);
    const base = {
        ...state,
        score: state.score + task.score,
        selectedIds: [],
        journal,
        log: task.success
    };
    if (nextTaskIndex >= chapter.tasks.length) {
        return {
            ...base,
            phase: state.chapterIndex >= chapters.length - 1 ? "finished" : "chapter-complete",
            completedChapterIds: uniqueAppend(state.completedChapterIds, chapter.id),
            journal: uniqueAppend(journal, `${chapter.badge}: ${chapter.journalUnlock}`),
            log: state.chapterIndex >= chapters.length - 1
                ? "Campaign complete. The Research Journal has enough signal to start the real analysis."
                : `${chapter.badge} unlocked. Choose the next chapter when ready.`
        };
    }
    return {
        ...base,
        taskIndex: nextTaskIndex
    };
}
function nextChapter(state) {
    if (state.chapterIndex >= chapters.length - 1) {
        return {
            ...state,
            phase: "finished",
            log: "All chapters complete. Replay any chapter or start a new run."
        };
    }
    const chapterIndex = state.chapterIndex + 1;
    return {
        ...state,
        chapterIndex,
        taskIndex: 0,
        selectedIds: [],
        phase: "playing",
        log: `Chapter ${chapterIndex + 1} loaded: ${chapters[chapterIndex]?.title ?? "Unknown"}.`
    };
}
export function createPlayer(id, name, index) {
    return {
        id,
        name: cleanName(name) || `Player ${index + 1}`,
        role: playerRoles[index % playerRoles.length],
        color: playerColors[index % playerColors.length] ?? "#7bd88f"
    };
}
export function createSnapshot(state, players, roomId, modeLabel) {
    const chapter = getCurrentChapter(state);
    const task = getCurrentTask(state);
    return {
        ...state,
        gameTitle: GAME_TITLE,
        roomId,
        modeLabel,
        players,
        chapter,
        task,
        progressLabel: `Chapter ${state.chapterIndex + 1}/${chapters.length} - Task ${state.taskIndex + 1}/${chapter.tasks.length}`
    };
}
export function validateContent() {
    const errors = [];
    chapters.forEach((chapter, chapterIndex) => {
        if (chapter.tasks.length < 1) {
            errors.push(`Chapter ${chapterIndex + 1} has no tasks.`);
        }
        chapter.tasks.forEach((task) => {
            const choiceIds = new Set(task.choices.map((choice) => choice.id));
            task.correctIds.forEach((id) => {
                if (!choiceIds.has(id)) {
                    errors.push(`${chapter.id}/${task.id} references missing choice ${id}.`);
                }
            });
        });
    });
    return errors;
}
function sameSet(left, right) {
    if (left.length !== right.length) {
        return false;
    }
    const l = new Set(left);
    return right.every((item) => l.has(item));
}
function uniqueAppend(list, item) {
    return list.includes(item) ? list : [...list, item];
}
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
function cleanName(name) {
    return name.trim().replace(/[^\w .-]/g, "").slice(0, 24);
}
